import { formatCurrency, formatNumber } from "@angular/common";
import { AfterViewChecked, Component, DoCheck, ElementRef, EventEmitter, HostListener, Input, LOCALE_ID, OnChanges, OnInit, Output, SimpleChanges, ViewChild, OnDestroy, inject } from "@angular/core";
import { AbstractControl, ControlContainer, NgModelGroup } from "@angular/forms";
import { KeyHandlerService, SecurityService, ToastService } from "@tylertech/core-module";
import { Utilities } from "@tylertech/shared-module";
import { PropertyMetadata } from "@tylertech/tyler-api-types";
import { Subject } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";
import { AugmentedModelObject, ValueChangedParms } from "../../../../interfaces";
import { MetadataEventService, MetadataInputTypeHelperService, MetadataPropertyService, MetadataService, MetadataValidationService } from "../../../../services";

@Component({
    selector: "metadata-input-number",
    templateUrl: "./metadata-input-number.tmpl.html",
    viewProviders: [{ provide: ControlContainer, useExisting: NgModelGroup }],
    standalone: false
})
export class MetadataInputNumberComponent implements OnInit, AfterViewChecked, DoCheck, OnChanges, OnDestroy
{
    modelGroup = inject(NgModelGroup);
    private readonly $metadataService = inject(MetadataService);
    private readonly $metadataInputTypeHelperService = inject(MetadataInputTypeHelperService);
    private readonly $metadataPropertyService = inject(MetadataPropertyService);
    private readonly $metadataValidationService = inject(MetadataValidationService);
    private readonly $metadataEventService = inject(MetadataEventService);
    private readonly $keyHandlerService = inject(KeyHandlerService);
    private readonly $toastService = inject(ToastService);
    private readonly $securityService = inject(SecurityService);
    locale = inject(LOCALE_ID);

    @Input()
    model: number;

    @Input()
    metadataModel: AugmentedModelObject; // model should be a field on metadataModel, FYI

    @Input()
    parent: AugmentedModelObject; // metadataModel should be a field on parent, FYI

    @Input()
    propertyMetadata: PropertyMetadata;

    @Input()
    viewOnly: boolean;

    @Input()
    dense: boolean;

    @Input()
    forCellEdit: boolean;

    @Output()
    onValueChanged = new EventEmitter<ValueChangedParms>();

    @Input()
    hint: string;

    @Input()
    error: string;

    @Input()
    compact: boolean;

    @Input()
    showSpinner: boolean;

    @ViewChild("input") input: ElementRef;

    /** Accessible in the template */ isViewOnly: boolean;

    private isInitialized: boolean;

    /**
     * Metadata's Recalculate on change functionality mutates the metadataModel in a way that will not trigger a binding change event,
     * so we need to have a way to do a simple check in `$doCheck` to determine if the `this.model` value was changed with out our notice.
     * This should only be set in `$onInit` and `onModelChanged`.
     */
    private unboundModel: number;

    private _valueModel: string;

    private toastDisplayed = false;

    private readonly debouncer$ = new Subject<void>();
    private readonly destroy$ = new Subject<void>();

    private _density: string;

    onBlur(event: any): void
    {
        if (!this.forCellEdit)
            this.fireValueChanged(true);

        const parsedModel = this.parse(this.valueModel);
        const formattedModel = this.format(this.valueModel);
        const parsedFormattedModel = this.parse(formattedModel);

        if (parsedModel !== parsedFormattedModel)
        {
            this.valueModel = parsedFormattedModel;
            if (!this.forCellEdit)
                this.fireValueChanged();
        }

        this.valueModel = formattedModel;
    }

    changedByParse: boolean = false;

    @HostListener("focusin", ["$event"])
    onFocus(event: any): void
    {
        this.valueModel = this.parse(this.valueModel);
        if (this.changedByParse)
            requestAnimationFrame(() => this.input.nativeElement.select());
    }

    ngOnInit(): void
    {
        const strippedModel = Utilities.IsNullOrUndefined(this.model) ? null : this.parse(this.model.toString());
        this.unboundModel = parseFloat(strippedModel);
        this.valueModel = this.format(strippedModel);

        if (this.forCellEdit)
        {
            this.debouncer$.pipe(debounceTime(10), takeUntil(this.destroy$)).subscribe(() => this.fireValueChanged());
        }
        const self = this;
        if (this.propertyMetadata["$listenForNonPropertyChange"])
        {
            this.$metadataEventService.onRecalculateWorking.pipe(takeUntil(this.destroy$))
                .subscribe(args =>
                {
                    if (!args.Recalculating)
                    {
                        self.control.markAsTouched();
                        self.runValidatorsOnChange();
                    }
                });
        }

        this._density = this.$metadataService.getInputDensity(this.compact);
    }

    ngOnDestroy(): void
    {
        this.input = null;
        this.destroy$.next();
        this.destroy$.complete();
        if (this.forCellEdit)
        {
            this.debouncer$.complete();
        }
    }

    ngAfterViewChecked(): void
    {
        if (this.isInitialized)
            return;

        if (this.control)
        {
            this.isInitialized = true;
            this.validators(this.valueModel);
        }
    }

    ngDoCheck(): void
    {
        if (!this.isInitialized)
        {
            return;
        }
        const model = isNaN(this.model) ? 0 : this.model;
        if (this.unboundModel !== model)
        {
            this.onModelChanged(this.model);
        }
        this.isViewOnly =
            (this.viewOnly && !this.propertyMetadata.ViewOnlyEnabled) ||
            this.$metadataPropertyService.isViewOnly(this.propertyMetadata, this.metadataModel, this.parent);
        if (this.control["$runValidation"])
        {
            delete this.control["$runValidation"];
            this.runValidatorsOnChange();
        }
    }

    ngOnChanges(_changes: SimpleChanges): void
    {
        const changedKeys = Object.keys(_changes);
        for (const key of changedKeys)
        {
            switch (key)
            {
                case "model":
                    const change = _changes[key];
                    this.onModelChanged(change.currentValue);
                    break;
            }
        }
    }

    get control(): AbstractControl
    {
        return this.modelGroup?.control?.controls[this.propertyMetadata.Name];
    }

    get isInvalid(): boolean
    {
        return this.$metadataService.invalidInput(this.error, this.control);
    }

    private onModelChanged(value: number): void
    {
        this.unboundModel = isNaN(value) ? 0 : value;
        if (value !== 0 && !value)
        {
            this.valueModel = null;
            return;
        }
        const valueStr = value.toString();
        if (!this.valueModel || (this.valueModel !== valueStr && !this.decimalEquivalence(valueStr, this.valueModel)))
        {
            this.valueModel = this.format(valueStr);
        }
    }

    private decimalEquivalence(valueStr: string, valueModel: string): boolean
    {
        if (Utilities.IsNullOrUndefinedOrEmpty(valueStr) || Utilities.IsNullOrUndefinedOrEmpty(valueModel))
        {
            return false;
        }

        const zeroCount = valueModel.split("0").length - 1;
        const allZerosExceptDecimal = zeroCount === valueModel.length - 1 && valueModel.startsWith(".");
        const allZerosExceptDecimalAndNegative = zeroCount === valueModel.length - 2 && valueModel.startsWith("-.");
        return (valueStr === "0" && (allZerosExceptDecimal || allZerosExceptDecimalAndNegative))
            || (valueModel.startsWith(".") && valueStr === "0" + valueModel)
            || (valueModel.startsWith("-.") && valueStr === "-0" + valueModel.substring(1));
    }

    get valueModel(): string
    {
        return this._valueModel;
    }

    set valueModel(value: string)
    {
        if (Utilities.IsNullOrUndefined(value) && Utilities.IsNullOrUndefined(this.model) && this.isRequired)
        {
            this._valueModel = null;
        }
        else
        {
            this._valueModel = value;
        }
    }

    get isRequired(): boolean
    {
        return (
            !this.isViewOnly &&
            this.$metadataPropertyService.isRequired(this.propertyMetadata, this.metadataModel, this.parent)
        );
    }

    get isDisabled(): boolean
    {
        return (
            this.isViewOnly ||
            this.$metadataPropertyService.isDisabled(this.propertyMetadata, this.metadataModel, this.parent)
        );
    }

    get name(): string
    {
        return this.propertyMetadata.Name;
    }

    get label(): string
    {
        return this.forCellEdit
            ? ""
            : this.$metadataService.EvaluateString(this.propertyMetadata.Label, this.parent, this.metadataModel);
    }

    get Density(): string
    {
        return this._density;
    }

    get LabelPosition(): string
    {
        return this.forCellEdit ? 'none' : this.compact ? 'block-start' : 'inset';
    }

    get isCurrency(): boolean
    {
        return this.$metadataInputTypeHelperService.isCurrency(this.propertyMetadata.InputType);
    }

    get isPercent(): boolean
    {
        return this.$metadataInputTypeHelperService.isPercent(this.propertyMetadata.InputType);
    }

    get isNumberWithoutFormatting(): boolean
    {
        return this.$metadataInputTypeHelperService.isNumberWithoutDisplayFiltering(this.propertyMetadata.InputType);
    }

    onInputChange(): void
    {
        if (this.forCellEdit)
            this.debouncer$.next();
    }

    onChange(): void
    {
        const formattedModel = this.format(this.valueModel);
        const parsedFormattedModel = this.parse(formattedModel);

        this.valueModel = parsedFormattedModel;
        if (!this.forCellEdit)
            this.fireValueChanged();

        this.valueModel = formattedModel;
    }

    fireValueChanged(validateOnly: boolean = false): void
    {
        if (!validateOnly && this.model === parseFloat(this.parse(this.valueModel)))
            return;
        let newValue: number;
        if (this.valueModel)
            newValue = parseFloat(this.valueModel);
        else if (this.isRequired)
        {
            if (!validateOnly)
                newValue = 0;
            if (this.model === 0)
                this.valueModel = "0";
        }
        else
            newValue = null;

        this.runValidators(newValue);

        if (!isNaN(newValue))
            this.onValueChanged.emit({ value: newValue, validateOnly: validateOnly });
    }

    runValidatorsOnChange(): void
    {
        let newValue: number;
        if (this.valueModel)
            newValue = parseFloat(this.valueModel);
        else if (this.isRequired)
            newValue = 0;
        else
            newValue = null;
        this.runValidators(newValue);
    }

    runValidators(newValue: number): void
    {
        const self = this;
        if (!(Utilities.IsNullOrUndefined(newValue) || isNaN(newValue)))
        {
            this.control.setErrors(null);
            requestAnimationFrame(() =>
            {
                const validValue = self.validators(this.valueModel);
                if (validValue)
                    self.error = "";
            });
        }
    }

    validators(newValue: string): boolean
    {
        if (!this.$metadataValidationService.validateMaximumAmount(newValue, this.isDisabled, this.maxValue, () => this.parse(newValue)))
        {
            this.error = `${this.label} cannot have a value more than ${this.maxValue}`;
            this.control.setErrors({ "maxValue": true });
            return false;
        }
        if (!this.$metadataValidationService.validateMinimumAmount(newValue, this.isDisabled, this.minValue, () => this.parse(newValue)))
        {
            this.error = `${this.label} cannot have a value less than ${this.minValue}`;
            this.control.setErrors({ "minValue": true });
            return false;
        }
        if (!this.$metadataValidationService.validatePrecision(newValue, this.isDisabled, this.precision))
        {
            this.error = `${this.label} cannot have more than ${this.precision} digits`;
            this.control.setErrors({ "precision": true });
            return false;
        }
        if (!this.$metadataValidationService.validateScale(newValue, this.isDisabled, this.scale))
        {
            this.error = `${this.label} cannot have more than ${this.scale} decimal places`;
            this.control.setErrors({ "scale": true });
            return false;
        }
        return true;
    }

    enforceFormat(event: KeyboardEvent): void
    {
        if (event.code === "Escape"
            || /F\d{1,2}/.test(event.code))
        {
            return;
        }

        if (event.code === "NumpadAdd" && this.$securityService.UseTenKeyPlusAsTab)
        {
            event.preventDefault();
            this.$keyHandlerService.selectNextTabbableElement();
        }
        else if (!this.isValidInput(event) && !this.$keyHandlerService.ignoreForFormatting(event, false))
        {
            this.showInvalidEntryMessage();
            event.preventDefault();
            this.valueModel = null;
            this.fireValueChanged();
        }
    }

    get minValue(): number
    {
        return this.$metadataInputTypeHelperService.getMinValue(this.propertyMetadata, this.metadataModel, this.parent);
    }

    get maxValue(): number
    {
        return this.$metadataInputTypeHelperService.getMaxValue(this.propertyMetadata, this.metadataModel, this.parent);
    }

    get precision(): number
    {
        return this.$metadataInputTypeHelperService.getPrecision(this.propertyMetadata, this.metadataModel, this.parent);
    }

    get scale(): number
    {
        return this.$metadataInputTypeHelperService.getScale(this.propertyMetadata, this.metadataModel, this.parent);
    }

    isValidInput(event): boolean
    {
        const key = event.key;
        const regex = new RegExp('^\\d$');
        const currentTarget = event.currentTarget.value;

        const isNumeric = regex.test(key);
        const isMinus = currentTarget.indexOf("-") < 0 && key === "-";
        const isDecimal = currentTarget.indexOf(".") < 0 && key === ".";

        return isNumeric || isMinus || isDecimal || key === "Shift";
    }

    checkFormat(event): void
    {
        const key = event.key;
        if (key === "-")
        {
            const currentTarget = event.currentTarget.value;
            if ((currentTarget.indexOf("-") > 0 && currentTarget.indexOf("-") < currentTarget.length - 1) || currentTarget.match(/-/g)?.length > 1)
            {
                event.currentTarget.value = currentTarget.replace(/-/g, "");
            }
            else if(currentTarget.indexOf("-") === currentTarget.length - 1)
            {
                event.currentTarget.value = "-" + currentTarget.substring(0, currentTarget.length - 1);
            }
            this.valueModel = event.currentTarget.value;
        }
    }

    handlePaste(event): void
    {
        const pastedText = event.clipboardData.getData('text/plain');
        this.valueModel = this.parse(pastedText);
        const isNumeric = !isNaN(Number(this.valueModel));

        if (!isNumeric)
        {
            this.showInvalidEntryMessage();
        }

        event.preventDefault();
        this.fireValueChanged();
    }

    private showInvalidEntryMessage(): void
    {
        this.valueModel = parseFloat(this.valueModel).toString();
        if (!this.toastDisplayed)
        {
            const self = this;
            this.toastDisplayed = true;

            this.$toastService.showToastPromise("Invalid non-numeric entry.", 2000, "error")
                .then(() =>
                {
                    self.toastDisplayed = false;
                });
        }
    }

    private format(value: string): string
    {
        if (Utilities.IsNullOrUndefinedOrEmpty(value) && parseFloat(value) !== 0)
        {
            return null;
        }

        const parsedValue = this.parse(value);
        let scale = Utilities.IsNullOrUndefined(this.scale) ? 3 : this.scale;

        if (this.isCurrency && !this.forCellEdit)
        {
            scale = Utilities.IsNullOrUndefined(this.scale) ? 2 : this.scale;
            return formatCurrency(parseFloat(parsedValue), this.locale, "$", null, `1.${scale}`);
        }

        if (this.isNumberWithoutFormatting)
        {
            return formatNumber(parseFloat(parsedValue), this.locale, `1.0-${scale}`).replace(/,/g, "");
        }
        let formattedValue = undefined;
        if (!Utilities.IsNullOrUndefined(this.scale))
        {
            if (this.forCellEdit)
            {
                formattedValue = parsedValue;
            }
            else
            {
                const filteredVal = formatNumber(parseFloat(parsedValue), this.locale, `1.${scale}-${scale}`);
                if (this.isExponentialNotation(filteredVal))
                {
                    formattedValue = parsedValue;
                }
                else
                {
                    formattedValue = filteredVal;
                }
            }
        }
        else
        {
            const parsedFloat = parseFloat(parsedValue);
            if (parsedFloat % 1 === 0)
                scale = 0;
            formattedValue = formatNumber(parsedFloat, this.locale, `1.${scale}-${scale}`);
        }

        if (this.isPercent)
        {
            formattedValue += "%";
        }

        return formattedValue;
    }

    private parse(value: string): string
    {
        if (this.isExponentialNotation(value)) return null;
        const source = value ? value.replace(/,/g, "")
            .replace(/\$/g, "")
            .replace(/%/g, "") : value;
        const results = /(-?(?:\d+(?:\.\d+)?|\.\d+))/.exec(source);
        const parsedValue = results && results.length > 0 ? results[1] : value;
        this.changedByParse = parsedValue !== this._valueModel;
        return parsedValue;
    }

    private isExponentialNotation(value: string): boolean
    {
        if (!Utilities.IsNullOrUndefined(value))
        {
            if (value.indexOf("e+") >= 0)
            {
                return true;
            }
        }
        return false;
    }
}
